import React, {Component} from 'react'
import {connect} from 'react-redux'
import propTypes from 'prop-types'
import {isEmpty, isNil} from 'lodash'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import Divider from '@material-ui/core/Divider'
import iconNames from '../../theme/icons'
import PivotTable from '../../containers/pivotTable'
import Icon from '../../components/icon'
import {pivotTablePropTypes} from 'containers/pivotTable'
import {getAllRows, getAreAllRowsSelected} from 'selectors/pivotTable'
import {deSelectAllRows, selectAllRows} from 'actions/pivotTable'
import {getRowIds, getTranslatedColumns, getTranslatedLabels} from "helpers/pivotTable"
import Translator from "components/translator"
import './selectablePivotTable.scss'

const classPrefix = 'selectablePivotTable'

class SelectablePivotTable extends Component {
  constructor(props) {
    super(props)
    this.state = {
      activeDataset: null,
      activeLevel: 0,
      actionsOpen: false,
      actionsAnchor: null,
    }
  }

  setActiveDataset = (activeDataset, activeLevel) => {
    this.setState({activeDataset, activeLevel})
  }

  getData = () => {
    const {data} = this.props
    const {activeDataset} = this.state
    return activeDataset || data
  }

  getSelectedData = () => {
    const {selectedRows} = this.props
    const selectedIds = getRowIds(selectedRows)
    return this.getData().filter(entity => selectedIds.includes(entity.id))
  }

  showActions = (e) => {
    this.setState({
      actionsOpen: true,
      actionsAnchor: e.currentTarget,
    })
  }

  hideActions = () => {
    this.setState({actionsOpen: false})
  }

  bulkAction = (e, action) => {
    if (action.onClick(e, this.getSelectedData()) !== false) this.hideActions()
  }

  selectAllRows = () => {
    const {onChangeSelectedRows, onCanSelect, onSelectAllRows} = this.props
    const selectedRowIds = getRowIds(this.getData().filter((entity, index) => {
      return !(onCanSelect && !onCanSelect(entity, index, 0))
    }))
    onSelectAllRows()
    if (onChangeSelectedRows) onChangeSelectedRows(selectedRowIds, true)
  }

  deSelectAllRows = () => {
    const {onChangeSelectedRows, onDeSelectAllRows} = this.props
    onDeSelectAllRows()
    if (onChangeSelectedRows) onChangeSelectedRows([], false)
  }

  toggleSelectedRow = (entity, startIndex, endIndex, toggled) => {
    const {
      data,
      onChangeSelectedRows,
      onDeSelectAllRows,
      onSelectAllRows,
      selectedRows,
    } = this.props

    let selectedIds = getRowIds(selectedRows)

    const exists = selectedIds.includes(entity.id)
    if (toggled) {
      if (!exists) selectedIds.push(entity.id)
      if (data.length === selectedIds.length) onSelectAllRows()
    } else if (exists) {
      selectedIds = selectedIds.filter(id => id !== entity.id)
      onDeSelectAllRows()
    }

    if (onChangeSelectedRows)
      onChangeSelectedRows(selectedIds)
  }

  translateActions = (actions) => {
    const {selectedRows} = this.props

    const selectedIds = getRowIds(selectedRows)

    if (actions) {
      return actions.map(action => ({
        ...action,
        disabled: action.disabled || (action.selectAction && selectedIds.length === 0),
        onClick: action.onClick ? (e) => action.onClick(e, this.getSelectedData()) : null,
        selectAction: null,
        actions: this.translateActions(action.actions),
      }))
    }
  }

  render() {
    const {
      areAllRowsSelected,
      actions,
      onCanSelect,
      canSelectRows,
      bulkActions,
      addLabel,
      columns,
      data,
      highlight,
      icon,
      listEntityClass,
      onPost,
      onSelectEntity,
      pagingAllowed,
      pluralNoun,
      showAdd,
      singularNoun,
      stateKey,
      stickyTop,
      onAdd,
      footerRight,
      selectedRows,
      isAddButtonDisabled,
    } = this.props
    const {activeLevel, actionsOpen, actionsAnchor} = this.state

    const selectedIds = getRowIds(selectedRows)
    const totalRows = selectedIds.length

    const anySelectable = (
      isNil(onCanSelect) ||
      this.getData().some((entity, index) => onCanSelect(entity, index, 0))
    )
    const itemsText = (totalRows === 1) ? '1 item' : `${totalRows} items`
    const actionsDisabled = (totalRows === 0)

    let selectActions = []
    if (anySelectable && bulkActions && bulkActions.length > 0) {
      selectActions = [
        ...bulkActions.map((action, index) => {
          if (action.divider) {
            return <Divider key={index} className={`${classPrefix}-divider`}/>
          }
          const label = action.label.replace(/{items}/g, itemsText)
          return (
            <MenuItem
              key={index}
              onClick={(e) => this.bulkAction(e, action)}
            >
              <ListItemIcon><Icon name={action.icon} color={action.iconColor}/></ListItemIcon>
              {label}
            </MenuItem>
          )
        }),
      ]
    }

    // Selectable rows as long as something is selectable
    const hasSelectAction = isEmpty(actions) ? false : actions.some(action => action.selectAction)
    const _canSelectRows = anySelectable && (canSelectRows || selectActions.length > 0 || hasSelectAction)

    let ptActions = []
    if (!isEmpty(selectActions)) {
      ptActions.push({
        label: 'Actions',
        icon: iconNames.dropdown,
        size: 12,
        labelPosition: 'before',
        disabled: actionsDisabled,
        onClick: this.showActions,
      })
    }

    if (!isEmpty(actions)) {
      if (!isEmpty(selectActions))
        ptActions.push({divider: true})

      ptActions = [
        ...ptActions,
        ...this.translateActions(actions),
      ]
    }
    return (
      <Translator text={getTranslatedLabels(columns)} onRender={(translatedColumns) => (
        <div className={`${classPrefix}-container`}>
          <PivotTable
            addLabel={addLabel}
            columns={getTranslatedColumns(columns, translatedColumns)}
            data={data}
            highlight={highlight}
            icon={icon}
            listEntityClass={listEntityClass}
            onPost={onPost}
            onSelectEntity={onSelectEntity}
            pagingAllowed={pagingAllowed}
            pluralNoun={pluralNoun}
            showAdd={showAdd}
            singularNoun={singularNoun}
            stateKey={stateKey}
            stickyTop={stickyTop}
            onAdd={onAdd}
            className={classPrefix}
            actions={ptActions}
            footerRight={footerRight}
            canSelectRows={_canSelectRows}
            areAllRowsSelected={areAllRowsSelected}
            onSelectAllRows={this.selectAllRows}
            onDeSelectAllRows={this.deSelectAllRows}
            onToggleSelectedRow={this.toggleSelectedRow}
            onIsRowSelected={(entity, index, level) => (level === activeLevel && selectedIds.includes(entity.id))}
            onCanSelect={onCanSelect}
            onChangeActiveDataset={this.setActiveDataset}
            isAddButtonDisabled={isAddButtonDisabled}
          >
            {this.props.children}
          </PivotTable>
          <Menu
            open={actionsOpen}
            getContentAnchorEl={null}
            anchorOrigin={{horizontal: 'left', vertical: 'bottom'}}
            transformOrigin={{horizontal: 'left', vertical: 'top'}}
            anchorEl={actionsAnchor}
            onClose={this.hideActions}
            onClick={(e) => e.stopPropagation()}
            disableRestoreFocus={true}>{selectActions}</Menu>
        </div>
      )}/>
    )
  }
}

SelectablePivotTable.propTypes = {
  onCanSelect: propTypes.func,
  bulkActions: propTypes.arrayOf(propTypes.shape({
    label: propTypes.string,
    icon: propTypes.string,
    onClick: propTypes.func, // (items: array(number))
  })),
  onChangeSelectedRows: propTypes.func,
  ...pivotTablePropTypes,
}

const mapStateToProps = (state, ownProps) => {
  const {stateKey} = ownProps
  return {
    data: getAllRows(state, stateKey),
    areAllRowsSelected: getAreAllRowsSelected(state, stateKey),
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  return {
    onSelectAllRows: () => dispatch(selectAllRows(ownProps.stateKey)),
    onDeSelectAllRows: () => dispatch(deSelectAllRows(ownProps.stateKey)),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(SelectablePivotTable)
